import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Link } from 'react-router-dom';
import { getInventoryDashboard, getInventoryMovements } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Boxes, Store, Truck, PackageCheck, AlertTriangle, CheckCircle2, Package, History, QrCode } from 'lucide-react';
import { toast } from 'sonner';

const InventoryDashboard = ({ user, setUser }) => {
  const [stats, setStats] = useState({ totals: {}, by_category: {} });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [d, m] = await Promise.all([getInventoryDashboard(), getInventoryMovements({ limit: 15 })]);
        setStats(d.data);
        setRecent(m.data || []);
      } catch {
        toast.error('Failed to load inventory dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const t = stats.totals || {};
  const cats = stats.by_category || {};
  const bulkByType = stats.bulk_by_item_type || [];

  const kpis = [
    { label: 'Serialized Items', value: t.total_serialized_items || 0, icon: QrCode, color: 'bg-indigo-100 text-indigo-700', link: '/inventory/items' },
    { label: 'Bulk Stock (Units)', value: t.bulk_quantity_remaining || 0, icon: Boxes, color: 'bg-purple-100 text-purple-700', link: '/inventory/items?tab=bulk' },
    { label: 'Deployed', value: t.deployed || 0, icon: CheckCircle2, color: 'bg-green-100 text-green-700', link: '/inventory/items?status=deployed' },
    { label: 'In Stock', value: t.in_stock || 0, icon: Package, color: 'bg-blue-100 text-blue-700', link: '/inventory/items?status=in_stock' },
    { label: 'Assigned in Chain', value: t.assigned || 0, icon: Truck, color: 'bg-amber-100 text-amber-700', link: '/inventory/items?status=assigned' },
    { label: 'Inactive', value: t.inactive || 0, icon: AlertTriangle, color: 'bg-orange-100 text-orange-700', link: '/inventory/items?status=inactive' },
    { label: 'Lost / Scrapped', value: (t.lost || 0) + (t.scrapped || 0), icon: AlertTriangle, color: 'bg-red-100 text-red-700', link: '/inventory/items?status=scrapped' },
    { label: 'Merchants', value: t.total_merchants || 0, icon: Store, color: 'bg-emerald-100 text-emerald-700', link: '/inventory/merchants' },
    { label: 'Pending Returns', value: t.pending_returns || 0, icon: PackageCheck, color: 'bg-rose-100 text-rose-700', link: '/inventory/returns' },
  ];

  const quickLinks = [
    { to: '/inventory/items', label: 'View Inventory', icon: QrCode, desc: 'All serialized assets — Sound Box, POS, QR Standee' },
    { to: '/inventory/merchants', label: 'Merchants', icon: Store, desc: 'Onboarded merchants & their deployed assets' },
    { to: '/inventory/transfer', label: 'Issue / Transfer', icon: Truck, desc: 'Move inventory down the chain' },
    { to: '/inventory/returns', label: 'Returns', icon: PackageCheck, desc: 'Request return, replacement, scrap' },
    { to: '/inventory/movements', label: 'Audit Log', icon: History, desc: 'Full chain of custody' },
  ];

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="inventory-dashboard">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventory Dashboard</h1>
          <p className="text-sm text-slate-500">QR Standees · Sound Boxes · POS · Paper QR — chain of custody</p>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {kpis.map(k => {
            const Icon = k.icon;
            return (
              <Link
                key={k.label}
                to={k.link}
                className="block"
                data-testid={`kpi-link-${k.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Card className="border-slate-200 hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer h-full">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-slate-500">{k.label}</p>
                        <p className="text-2xl font-bold mt-1 text-slate-900 hover:text-indigo-700 transition-colors" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>{loading ? '...' : k.value}</p>
                      </div>
                      <div className={`p-2 rounded-lg ${k.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Bulk Stock by Item Type — bifurcation (Round 76) */}
        <Card data-testid="bulk-by-item-type-section">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Bulk Stock by Item Type</span>
              <span className="text-xs font-normal text-slate-500">
                Total: <b className="text-purple-700">{loading ? '...' : (t.bulk_quantity_remaining || 0)}</b> units
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {bulkByType.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                {loading ? 'Loading...' : 'No bulk stock available'}
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {bulkByType.map((b) => (
                  <Link
                    key={b.item_type_id}
                    to={`/inventory/items?tab=bulk&item_type=${b.item_type_id}`}
                    className="block"
                    data-testid={`bulk-type-link-${b.item_type_id}`}
                  >
                    <div
                      className="border border-purple-200 rounded-lg p-3 bg-purple-50 hover:bg-purple-100 hover:border-purple-400 transition-all cursor-pointer h-full"
                      data-testid={`bulk-type-${b.item_type_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-medium text-slate-700">{b.item_type_name}</p>
                          <p className="text-[10px] text-slate-500 capitalize">{(b.category || '').replace('_', ' ')}</p>
                        </div>
                        <Boxes className="h-4 w-4 text-purple-500" />
                      </div>
                      <p className="text-2xl font-bold text-purple-700 mt-2">
                        {(b.quantity_remaining || 0).toLocaleString()}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category breakdown */}
        <Card>
          <CardHeader><CardTitle className="text-base">Stock by Category</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {['qr_standee', 'sound_box', 'pos_machine', 'paper_qr', 'other'].map(cat => (
                <Link
                  key={cat}
                  to={`/inventory/items?category=${cat}`}
                  className="block"
                  data-testid={`category-link-${cat}`}
                >
                  <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-300 transition-all cursor-pointer h-full">
                    <p className="text-xs text-slate-500 capitalize">{cat.replace('_', ' ')}</p>
                    <p className="text-xl font-semibold text-slate-900 mt-1 hover:text-indigo-700">{cats[cat] || 0}</p>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick links */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {quickLinks.map(l => {
            const Icon = l.icon;
            return (
              <Link key={l.to} to={l.to} className="block" data-testid={`quicklink-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardContent className="p-4">
                    <Icon className="h-6 w-6 text-indigo-600 mb-2" />
                    <p className="font-semibold text-sm text-slate-900">{l.label}</p>
                    <p className="text-xs text-slate-500 mt-1">{l.desc}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Recent movements */}
        <Card>
          <CardHeader><CardTitle className="text-base">Recent Movements</CardTitle></CardHeader>
          <CardContent>
            {recent.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No movements yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-600">When</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-600">Type</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-600">Item</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-600">Qty</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-600">From → To</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-600">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map(m => (
                      <tr key={m.id} className="border-b border-slate-100">
                        <td className="py-2 px-3 text-xs text-slate-600">{new Date(m.created_at).toLocaleString()}</td>
                        <td className="py-2 px-3"><span className="px-2 py-0.5 text-xs rounded bg-indigo-50 text-indigo-700">{m.movement_type}</span></td>
                        <td className="py-2 px-3 text-slate-900">{m.serial_number || m.item_type_name || '-'}</td>
                        <td className="py-2 px-3 text-slate-700">{m.quantity}</td>
                        <td className="py-2 px-3 text-xs text-slate-600">{(m.from_holder_name || m.from_holder_type || '-')} → {(m.to_holder_name || m.to_holder_type)}</td>
                        <td className="py-2 px-3 text-xs text-slate-600">{m.triggered_by_user_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default InventoryDashboard;
